import { AlertTriangle, Check, Lock, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Controller } from "react-hook-form";
import { z } from "zod";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlertDialog } from "@/shared/components/feedback/ColocaAlertDialog";
import { ColocaInput } from "@/shared/components/forms/ColocaInput";
import { ColocaTextarea } from "@/shared/components/forms/ColocaTextarea";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { colocaToast } from "@/shared/utils/toast";

import { ACCESS_CATALOG } from "../../constants/accessCatalog.constants";
import { policiesService } from "../../services/policiesService";
import type { AccessPolicy, AccessStatement, PolicyEffect } from "../../types/access.types";
import { expandAction } from "../../utils/denyFirst";
import { AccessScopeBadge } from "./AccessScopeBadge";

export interface PolicyEditorDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The policy being edited/viewed, or `null` when creating a new one. */
  policy: AccessPolicy | null;
  /** View-only (protected policy, or global for an org user, or no permission). */
  readOnly: boolean;
  /** How many roles link this policy — gates deletion (backend returns 409 if > 0). */
  usedByCount: number;
  canDelete: boolean;
  onSaved: () => void;
  onDeleted: () => void;
}

/** `${moduleId}|${atom}` — `|` never appears in a Module id (which uses `:`). */
const cellKey = (resource: string, atom: string) => `${resource}|${atom}`;

/**
 * Create / edit / view a policy. A policy bundles `(resource, action)` statements under a single effect
 * (allow or deny). Statements are picked from the real backend catalog (`Module` × atomic
 * `read/write/delete`). The backend requires at least one statement. Protected policies — and global ones for
 * org users — open read-only.
 *
 * Text fields are RHF + Zod (module pattern, see `CreateApiKeyForm`); the limits mirror the backend
 * `CreatePolicyDto` (name ≤ 120, description ≤ 500). The effect radio and the statement grid are
 * intentionally NOT form state — they are toggles over the catalog, kept as local state.
 */
export function PolicyEditorDrawer({
  open,
  onOpenChange,
  policy,
  readOnly,
  usedByCount,
  canDelete,
  onSaved,
  onDeleted,
}: PolicyEditorDrawerProps) {
  const { t } = useTranslations("settings");

  const schema = useMemo(
    () =>
      z.object({
        name: z
          .string()
          .trim()
          .min(1, t("access_policy_name_required"))
          .max(120, t("access_validation_name_max")),
        description: z.string().trim().max(500, t("access_validation_desc_max")),
      }),
    [t],
  );

  const form = useZodForm(schema, { name: "", description: "" });
  const nameValue = form.watch("name");

  const [effect, setEffect] = useState<PolicyEffect>("allow");
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  // Statement-picker validation only — the text fields are validated by the Zod schema.
  const [statementsError, setStatementsError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Seed when the drawer opens. Existing statements are expanded to atomic cells (`update`→write,
  // `admin`→r/w/d) so they map onto the grid; saving re-emits them as atomic statements. `policy` is parent
  // state that only changes together with `open`, so this runs exactly once per open.
  useEffect(() => {
    if (!open) return;
    form.reset({ name: policy?.name ?? "", description: policy?.description ?? "" });
    setEffect(policy?.effect ?? "allow");
    const cells = new Set<string>();
    for (const statement of policy?.statements ?? []) {
      for (const atom of expandAction(statement.action)) {
        cells.add(cellKey(statement.resource, atom));
      }
    }
    setSelected(cells);
    setStatementsError(null);
  }, [open, policy, form]);

  const toggle = (resource: string, atom: string) => {
    if (readOnly) return;
    setSelected((prev) => {
      const next = new Set(prev);
      const key = cellKey(resource, atom);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    if (statementsError) setStatementsError(null);
  };

  const statements = useMemo<AccessStatement[]>(
    () =>
      [...selected].map((key) => {
        const sep = key.lastIndexOf("|");
        return { resource: key.slice(0, sep), action: key.slice(sep + 1) as AccessStatement["action"] };
      }),
    [selected],
  );

  const onValid = async (values: z.output<typeof schema>) => {
    if (selected.size === 0) {
      setStatementsError(t("access_policy_statements_required"));
      return;
    }
    setIsSaving(true);
    setStatementsError(null);
    try {
      const body = {
        name: values.name,
        description: values.description || undefined,
        effect,
        statements,
      };
      if (policy) await policiesService.update(policy.id, body);
      else await policiesService.create(body);
      colocaToast.success(policy ? t("access_policy_updated") : t("access_policy_created"));
      onSaved();
    } catch (e) {
      // A 409 on save is a duplicate name (the only conflict on create/update).
      colocaToast.error(
        HttpApiService.isConflictError(e) ? t("access_policy_name_taken") : HttpApiService.getErrorMessage(e),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!policy) return;
    try {
      await policiesService.remove(policy.id);
      colocaToast.success(t("access_policy_deleted"));
      onDeleted();
    } catch (e) {
      // 409 = still linked to a role (race vs. the proactive disable below).
      colocaToast.error(
        HttpApiService.isConflictError(e)
          ? t("access_policy_delete_in_use")
          : HttpApiService.getErrorMessage(e),
      );
    }
  };

  const title = policy
    ? readOnly
      ? t("access_policy_editor_view")
      : t("access_policy_editor_edit")
    : t("access_policy_editor_new");
  const deleteBlocked = usedByCount > 0;

  return (
    <ColocaDrawer
      open={open}
      onOpenChange={onOpenChange}
      size="xl"
      title={
        <span className="flex items-center gap-2.5">
          {readOnly && <Lock size={14} className="text-muted-foreground" />}
          {title}
          {policy && <AccessScopeBadge organizationId={policy.organization_id} />}
        </span>
      }
      description={policy ? policy.name : t("access_policy_editor_new_sub")}
      footer={
        <div className="flex w-full items-center gap-2">
          {policy && canDelete && !readOnly && (
            <ColocaButton
              variant="ghost"
              size="sm"
              icon={Trash2}
              className="text-destructive"
              disabled={deleteBlocked}
              title={deleteBlocked ? t("access_policy_delete_disabled_hint") : undefined}
              onClick={() => setDeleteOpen(true)}
            >
              {t("access_policy_delete")}
            </ColocaButton>
          )}
          <div className="ml-auto flex items-center gap-2">
            <span className="font-mono text-[11px] text-muted-foreground">
              {selected.size === 1
                ? t("access_policy_permits_singular")
                : t("access_policy_permits_plural", { count: selected.size })}
            </span>
            <ColocaButton
              variant={readOnly ? "default" : "ghost"}
              size="sm"
              onClick={() => onOpenChange(false)}
            >
              {readOnly ? t("access_drawer_close") : t("access_policy_cancel")}
            </ColocaButton>
            {!readOnly && (
              <ColocaButton
                variant="primary"
                size="sm"
                icon={Check}
                loading={isSaving}
                disabled={!nameValue.trim() || selected.size === 0}
                onClick={form.handleSubmit(onValid)}
              >
                {policy ? t("access_policy_save") : t("access_policy_create_action")}
              </ColocaButton>
            )}
          </div>
        </div>
      }
    >
      {readOnly && (
        <div className="mb-4 flex items-center gap-2.5 rounded-md border border-warning/40 bg-warning/10 px-3 py-2.5 text-[12px] text-foreground">
          <Lock size={12} className="text-warning" />
          {t("access_policy_readonly_note")}
        </div>
      )}

      {/* Identity + effect — text fields are RHF + Zod (see CreateApiKeyForm for the module pattern) */}
      <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-[1fr_260px]">
        <div className="flex flex-col gap-3">
          <Controller
            control={form.control}
            name="name"
            render={({ field, fieldState }) => (
              <div>
                <label
                  htmlFor="policy-editor-name"
                  className="mb-1 block text-[12.5px] font-medium text-foreground"
                >
                  {t("access_policy_name_label")}
                </label>
                <ColocaInput
                  id="policy-editor-name"
                  ref={field.ref}
                  value={field.value}
                  onValueChange={field.onChange}
                  onBlur={field.onBlur}
                  disabled={readOnly}
                  placeholder={t("access_policy_name_placeholder")}
                  aria-invalid={!!fieldState.error}
                />
                {fieldState.error && (
                  <p className="mt-1 flex items-center gap-1.5 text-[11.5px] text-destructive">
                    <AlertTriangle size={12} /> {fieldState.error.message}
                  </p>
                )}
              </div>
            )}
          />
          <Controller
            control={form.control}
            name="description"
            render={({ field, fieldState }) => (
              <div>
                <label
                  htmlFor="policy-editor-desc"
                  className="mb-1 block text-[12.5px] font-medium text-foreground"
                >
                  {t("access_policy_desc_label")}
                </label>
                <ColocaTextarea
                  id="policy-editor-desc"
                  ref={field.ref}
                  value={field.value}
                  onValueChange={field.onChange}
                  onBlur={field.onBlur}
                  disabled={readOnly}
                  rows={2}
                  placeholder={t("access_policy_desc_placeholder")}
                  aria-invalid={!!fieldState.error}
                />
                {fieldState.error && (
                  <p className="mt-1 flex items-center gap-1.5 text-[11.5px] text-destructive">
                    <AlertTriangle size={12} /> {fieldState.error.message}
                  </p>
                )}
              </div>
            )}
          />
        </div>

        <div>
          <div
            id="policy-editor-effect-label"
            className="mb-1 block text-[12.5px] font-medium text-foreground"
          >
            {t("access_policy_effect_label")}
          </div>
          <div role="radiogroup" aria-labelledby="policy-editor-effect-label" className="flex flex-col gap-2">
            {(["allow", "deny"] as const).map((e) => {
              const on = effect === e;
              const isDeny = e === "deny";
              return (
                <button
                  key={e}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  disabled={readOnly}
                  onClick={() => setEffect(e)}
                  className={cn(
                    "flex items-center gap-2.5 rounded-md border px-3 py-2 text-left transition-colors",
                    on
                      ? isDeny
                        ? "border-destructive bg-destructive/10"
                        : "border-primary bg-primary/10"
                      : "border-border bg-card hover:border-border/80",
                    readOnly && "cursor-default",
                  )}
                >
                  <span
                    className={cn(
                      "grid h-4 w-4 shrink-0 place-items-center rounded-full border",
                      on
                        ? isDeny
                          ? "border-destructive bg-destructive"
                          : "border-primary bg-primary"
                        : "border-border",
                    )}
                  >
                    {on && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
                  </span>
                  <span>
                    <span
                      className={cn(
                        "block text-[12.5px] font-semibold",
                        on ? (isDeny ? "text-destructive" : "text-primary") : "text-foreground",
                      )}
                    >
                      {isDeny ? t("access_policy_effect_deny_title") : t("access_policy_effect_allow_title")}
                    </span>
                    <span className="block text-[11px] text-muted-foreground">
                      {isDeny ? t("access_policy_effect_deny_hint") : t("access_policy_effect_allow_hint")}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Statement picker */}
      <div className="mb-2.5 flex items-center gap-2 border-b border-dashed border-border pb-2">
        <span className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.06em] text-foreground">
          {t("access_policy_statements_label")}
        </span>
        <span className="ml-auto text-[11px] text-muted-foreground">
          {t("access_policy_statements_hint")}
        </span>
      </div>

      {statementsError && (
        <p className="mb-2 flex items-center gap-1.5 text-[11.5px] text-destructive">
          <AlertTriangle size={12} /> {statementsError}
        </p>
      )}

      <div className="flex flex-col gap-1">
        {ACCESS_CATALOG.map((mod) => {
          const ModIcon = mod.icon;
          const onCount = mod.actions.filter((a) => selected.has(cellKey(mod.id, a))).length;
          return (
            <div key={mod.id} className="flex items-center gap-2 py-0.5">
              <div
                className={cn(
                  "grid h-[22px] w-[22px] shrink-0 place-items-center rounded border",
                  onCount > 0
                    ? effect === "deny"
                      ? "border-destructive/40 bg-destructive/10 text-destructive"
                      : "border-primary/30 bg-primary/10 text-primary"
                    : "border-border text-muted-foreground",
                )}
              >
                <ModIcon size={12} />
              </div>
              <span
                className="w-[150px] shrink-0 truncate text-[11.5px] font-medium text-foreground"
                title={mod.id}
              >
                {t(mod.labelKey)}
              </span>
              <div className="ml-auto flex flex-wrap justify-end gap-1">
                {mod.actions.map((atom) => {
                  const on = selected.has(cellKey(mod.id, atom));
                  const risky = mod.risky?.[atom];
                  return (
                    <button
                      key={atom}
                      type="button"
                      role="checkbox"
                      aria-checked={on}
                      disabled={readOnly}
                      onClick={() => toggle(mod.id, atom)}
                      className={cn(
                        "inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 font-mono text-[10px] transition-colors",
                        on
                          ? effect === "deny"
                            ? "border-destructive/50 bg-destructive/10 text-destructive"
                            : "border-primary/40 bg-primary/10 text-primary"
                          : "border-border text-muted-foreground hover:border-border/70",
                        readOnly && "cursor-default",
                      )}
                    >
                      {on && (effect === "deny" ? <X size={9} /> : <Check size={9} />)}
                      {atom}
                      {risky && <AlertTriangle size={9} className="text-warning/80" />}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {policy && (
        <ColocaAlertDialog
          trigger={<span style={{ display: "none" }} />}
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          media={<Trash2 size={24} className="text-destructive" />}
          title={t("access_policy_delete_title")}
          description={t("access_policy_delete_desc", { name: policy.name })}
          confirmLabel={t("access_policy_delete_confirm")}
          cancelLabel={t("access_policy_cancel")}
          confirmVariant="destructive"
          onConfirm={handleDelete}
          onCancel={() => setDeleteOpen(false)}
        />
      )}
    </ColocaDrawer>
  );
}
